import { animate, motion, useInView, useReducedMotion, type Variants } from 'motion/react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from './ui'

export const YUMUSAK: [number, number, number, number] = [0.22, 1, 0.36, 1]

/** Görünüme girince yukarı süzülerek belirir. Bir kez. */
export function Reveal({
  children,
  delay = 0,
  y = 18,
  className,
  once = true,
}: {
  children: ReactNode
  delay?: number
  y?: number
  className?: string
  once?: boolean
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: '-10% 0px -10% 0px' }}
      transition={{ duration: 0.7, ease: YUMUSAK, delay }}
    >
      {children}
    </motion.div>
  )
}

const kapVaryant: Variants = {
  gizli: {},
  gorunur: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
}
const ogeVaryant: Variants = {
  gizli: { opacity: 0, y: 16 },
  gorunur: { opacity: 1, y: 0, transition: { duration: 0.6, ease: YUMUSAK } },
}

/** Çocukları sırayla belirten kap; çocuklar `StaggerItem` olmalı. */
export function Stagger({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div
      className={className}
      variants={kapVaryant}
      initial="gizli"
      whileInView="gorunur"
      viewport={{ once: true, margin: '-10% 0px' }}
    >
      {children}
    </motion.div>
  )
}

export function StaggerItem({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div className={className} variants={ogeVaryant}>
      {children}
    </motion.div>
  )
}

/** Görünüme girince sıfırdan hedefe sayar. */
export function Sayac({
  deger,
  onek = '',
  sonek = '',
  sure = 1.4,
  className,
}: {
  deger: number
  onek?: string
  sonek?: string
  sure?: number
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const gorunur = useInView(ref, { once: true, margin: '-20% 0px' })
  const azHareket = useReducedMotion()
  const [n, setN] = useState(0)
  useEffect(() => {
    if (!gorunur) return
    // Az hareket tercihinde süre sıfır: değer anında yerine oturur. Yine de
    // aynı yoldan geçer ki sunucu çıktısı (0) ile ilk çizim aynı kalsın.
    const k = animate(0, deger, {
      duration: azHareket ? 0 : sure,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setN(Math.round(v)),
    })
    return () => k.stop()
  }, [gorunur, deger, sure, azHareket])
  return (
    <span ref={ref} className={cn('data', className)}>
      {onek}
      {new Intl.NumberFormat('tr-TR').format(n)}
      {sonek}
    </span>
  )
}

/** Sonsuz kayan şerit. İçerik iki kez basılır; CSS ile döner. */
export function Marquee({
  children,
  saniye = 40,
  className,
}: {
  children: ReactNode
  saniye?: number
  className?: string
}) {
  return (
    <div className={cn('marquee overflow-hidden', className)}>
      <motion.div
        className="flex w-max gap-10 pr-10"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: saniye, ease: 'linear', repeat: Infinity }}
      >
        <div className="flex shrink-0 items-center gap-10">{children}</div>
        <div className="flex shrink-0 items-center gap-10" aria-hidden>
          {children}
        </div>
      </motion.div>
    </div>
  )
}
